import { type AppRouter } from "@shared-server/api/app-router";
import { WORKSPACE_ID_HEADER } from "@shared-utils/constants";
import { env } from "@shared/env.mjs";
import { QueryClient } from "@tanstack/react-query";
import { httpLink, loggerLink, retryLink, TRPCClientError } from "@trpc/client";
import * as Sentry from "@sentry/nextjs";
import { createTRPCReact } from "@trpc/react-query";
import { QueryCache } from "@tanstack/react-query";
import superjson from "superjson";
import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server";

export const getTrpcLinks = (path: string, workspaceId?: string) => {
  return [
    retryLink({
      retry(opts) {
        // Only retry queries, not mutations
        if (opts.op.type !== "query") {
          return false;
        }

        // Check if this is a parsed tRPC error with a code
        const errorCode = (opts.error.data as { code?: string } | undefined)
          ?.code;

        // If we have a parsed error response, only retry on internal server errors (500)
        if (errorCode && errorCode !== "INTERNAL_SERVER_ERROR") {
          return false;
        }

        // If error.data is undefined, this is likely a network error or JSON parsing failure
        // (e.g., gateway timeout returning HTML instead of JSON). Always retry these.
        const isNetworkOrParsingError = !opts.error.data;

        // Retry up to 3 times for internal server errors or network/parsing errors
        const doRetry = opts.attempts <= 3;

        // Console log for debugging trace in Sentry
        if (doRetry) {
          console.info(
            `Performing retry #${opts.attempts} of 3`,
            isNetworkOrParsingError
              ? "(network/parsing error)"
              : "(server error)",
          );
        }

        return doRetry;
      },
      // Double every attempt, with max of 30 seconds (starting at 1 second)
      retryDelayMs: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    }),
    loggerLink({
      enabled: (opts) =>
        process.env.NODE_ENV === "development" &&
        typeof window !== "undefined" &&
        opts.direction === "down" &&
        opts.result instanceof Error,
    }),
    httpLink({
      url: `${getBaseUrl()}${path}`,
      headers: {
        ...(workspaceId && { [WORKSPACE_ID_HEADER]: workspaceId }),
      },
      transformer: superjson,
    }),
  ];
};

/**
Shared React Query client provider
*/

export const getQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Disable retrying queries since this delays the UI updates
        retry: false,
        staleTime: Infinity,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (error instanceof TRPCClientError) {
          // Ignore "Unauthorized for tRPC endpoint", since this could be just the user logging out, thus queries failing
          if (error.message.includes("Unauthorized for tRPC endpoint")) {
            return;
          }
          // Ignore "not valid JSON" errors - these are transient errors caused by gateway timeouts (504)
          // returning HTML error pages instead of JSON responses. The retryLink handles retries,
          // but if all retries fail we don't need to flood Sentry with these network issues.
          if (error.message.includes("is not valid JSON")) {
            console.warn(
              "tRPC request failed with non-JSON response (likely gateway timeout)",
              { queryKey: query.queryKey },
            );
            return;
          }
          // Ignore "Invalid CSRF" errors - these occur when the CSRF cookie expires or is cleared,
          // which is expected behavior. Users can resolve by refreshing the page.
          if (error.message.includes("Invalid CSRF")) {
            return;
          }
          console.error("onerror", error);
          Sentry.captureException(error, {
            extra: {
              message: "TRPC Client Error",
              query: JSON.stringify(query),
            },
          });
        } else if (error instanceof Error) {
          console.error("onerror", error);
          Sentry.captureException(error, {
            extra: {
              message: "TRPC Frontend Error",
              query: JSON.stringify(query),
            },
          });
        }
      },
    }),
  });
};

/**
 * Returns the base URL for the TRPC provider based on the environment.
 * If the code is running in the browser, it returns an empty string to use a relative URL.
 * If the code is running in a development server, it returns the localhost URL with the port number specified in the environment variables.
 * @returns {string} The base URL for the TRPC provider.
 */
const getBaseUrl = () => {
  if (typeof window !== "undefined") return ""; // browser should use relative url
  return `http://localhost:${env.FRONTEND_PORT}`; // dev SSR should use localhost
};

export const api = createTRPCReact<AppRouter>();

/**
 * Inference helper for inputs.
 *
 * @example type HelloInput = RouterInputs['example']['hello']
 */
export type RouterInputs = inferRouterInputs<AppRouter>;

/**
 * Inference helper for outputs.
 *
 * @example type HelloOutput = RouterOutputs['example']['hello']
 */
export type RouterOutputs = inferRouterOutputs<AppRouter>;
